import {
  createTranslationArray,
  getGraphDimension,
  getGraphValue,
  PERSONALITY_PAGE_STRUCTURES,
} from 'affiny-core/helpers/personalityReport';
import {
  IPersonalityReportGraph,
  IPersonalityReportPageStructure,
} from 'affiny-core/modules/personalityReport/types';
import { useTitlePosition } from 'affiny-desktop/src/hooks';
import { Box, ValueBar } from 'affiny-ui/src';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalityReportLayerParagraph } from './PersonalityReportLayerParagraph';
import { PersonalityReportLayerTitle } from './PersonalityReportLayerTitle';

interface IPersonalityReportLayerPersonalitySubChapter {
  graphs: IPersonalityReportGraph[];
  personalityIntroAnalysis?: string[];
  setTitle: (title: string) => void;
}

const PersonalityReportLayerPersonalitySubChapter = ({
  graphs,
  personalityIntroAnalysis,
  setTitle,
}: IPersonalityReportLayerPersonalitySubChapter) => {
  const { t, i18n } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);

  const mainGraphId = graphs[0]?.id;
  const baseTradKey = `personalityReport.personality.detailedPages.${mainGraphId}`;

  useTitlePosition({ setTitle, title: mainGraphId, titleRef });

  const pageStructure: IPersonalityReportPageStructure =
    PERSONALITY_PAGE_STRUCTURES[mainGraphId] || {};

  const intros = createTranslationArray(
    pageStructure.nbIntro,
    baseTradKey,
    'intro'
  );

  const outros = createTranslationArray(
    pageStructure.nbOutro,
    baseTradKey,
    'outro'
  );

  const persoAnalysis = personalityIntroAnalysis || graphs[0]?.analysis;

  return (
    <Box mb="xLarge" pb="xLarge">
      <div ref={titleRef}>
        <PersonalityReportLayerTitle isPrimary id={mainGraphId}>
          {t(`${baseTradKey}.title`)}
        </PersonalityReportLayerTitle>
      </div>
      {intros.map((tradKey, idx) => (
        <PersonalityReportLayerParagraph key={idx}>
          {t(tradKey)}
        </PersonalityReportLayerParagraph>
      ))}

      {i18n.exists(`${baseTradKey}.subtitle`) && (
        <PersonalityReportLayerTitle>
          {t(`${baseTradKey}.subtitle`)}
        </PersonalityReportLayerTitle>
      )}

      <Box pb="small">
        {graphs.map((graph, idx) => {
          return (
            getGraphDimension(graph) === 1 && (
              <ValueBar
                key={idx}
                title={t(
                  `personalityReport.personality.valueLabels.${graph.id}`
                )}
                value={getGraphValue(graph)}
                containerStyle={{ mb: 'large' }}
              />
            )
          );
        })}
      </Box>

      {persoAnalysis && persoAnalysis.length ? (
        <>
          <PersonalityReportLayerTitle>
            {t(`personalityReport.psyAnalysis`)}
          </PersonalityReportLayerTitle>
          {persoAnalysis.map((text, idx) => (
            <PersonalityReportLayerParagraph key={idx}>
              {text}
            </PersonalityReportLayerParagraph>
          ))}
        </>
      ) : null}

      {outros.map((trad, idx) => (
        <PersonalityReportLayerParagraph key={idx}>
          {t(trad)}
        </PersonalityReportLayerParagraph>
      ))}
    </Box>
  );
};

export default PersonalityReportLayerPersonalitySubChapter;
